/**
 * TUVI - Caja Menor (Módulo independiente)
 * Tabs: Residentes (entregas) | Comprobantes | Aprobaciones
 * Features:
 * - Entregas asignadas a residentes (sin proyecto)
 * - Vista por residente O por proyecto (toggle)
 * - Comprobantes con valor por proyecto (residente selecciona proyecto)
 * - Aprobaciones con filtros y búsqueda
 * - Alerta visual al 90%+ gastado
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import client from '../../services/graphqlClient';
import { BACKEND_URL, UPLOAD_URL } from '../../config';
import * as XLSX from 'xlsx-js-style';
import './CajaMenor.css';

// Normaliza nombre/apto para comparar proyectos con solicitudes de posventa.
const normPv = (s) => (s == null ? '' : String(s)).trim().toLowerCase().replace(/\s+/g, ' ');

const CajaMenor = () => {
  const { success, error } = useNotification();
  const { tienePermiso } = useAuth();
  const permisoIngenieros = tienePermiso('proyectos', 'caja-menor-ingenieros');
  const permisoCrearEntrega = tienePermiso('proyectos', 'caja-menor-crear-entrega');
  const permisoEliminarEntrega = tienePermiso('proyectos', 'caja-menor-eliminar-entrega');
  const permisoComprobantes = tienePermiso('proyectos', 'caja-menor-comprobantes');
  const permisoAprobaciones = tienePermiso('proyectos', 'caja-menor-aprobaciones');
  const [tabActiva, setTabActiva] = useState(() => {
    if (permisoIngenieros) return 'residentes';
    if (permisoComprobantes) return 'comprobantes';
    if (permisoAprobaciones) return 'aprobaciones';
    return 'residentes';
  });
  const [loading, setLoading] = useState(true);

  // Data
  const [residentes, setResidentes] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  // Proyectos que están en el módulo de posventa (por id vinculado y por nombre+apto).
  const [posventaSet, setPosventaSet] = useState({ ids: new Set(), keys: new Set() });
  // Apartamentos del módulo de posventa (nombre + # apto) para poder facturarles caja menor.
  const [posventaAptos, setPosventaAptos] = useState([]);
  const [entregas, setEntregas] = useState([]);
  const [resumenResidentes, setResumenResidentes] = useState([]);
  const [comprobantes, setComprobantes] = useState([]);
  const [conteos, setConteos] = useState({ pendiente: 0, aprobado: 0, rechazado: 0 });

  // Forms - Entrega (sin proyectos, solo residente + valor)
  const [mostrarFormEntrega, setMostrarFormEntrega] = useState(false);
  const [formEntrega, setFormEntrega] = useState({ residente_id: '', fecha: new Date().toISOString().split('T')[0], descripcion: '', valor: '' });

  // Forms - Comprobante
  const [mostrarFormComprobante, setMostrarFormComprobante] = useState(false);
  const [formComprobante, setFormComprobante] = useState({ residente_id: '', entrega_id: '', descripcion: '', fecha: new Date().toISOString().split('T')[0], proyectos: [], es_administrativo: false, valor_admin: '' });
  const [archivosComprobante, setArchivosComprobante] = useState([]);
  const [busquedaProyectoComp, setBusquedaProyectoComp] = useState('');

  // Filtros - Comprobantes tab
  const [filtroEstadoComp, setFiltroEstadoComp] = useState('pendiente');
  const [busquedaComp, setBusquedaComp] = useState('');
  const [fechaDesdeComp, setFechaDesdeComp] = useState('');
  const [fechaHastaComp, setFechaHastaComp] = useState('');

  // Aprobación
  const [aprobandoId, setAprobandoId] = useState(null);
  const [distribucion, setDistribucion] = useState([]);

  // Filtros - Residentes tab
  const [residenteSeleccionado, setResidenteSeleccionado] = useState(null);
  const [vistaEntregas, setVistaEntregas] = useState('residente'); // 'residente' | 'proyecto'
  const [filtroEstadoEntrega, setFiltroEstadoEntrega] = useState('todas'); // 'todas' | 'activas' | 'agotadas' | 'excedidas'
  const [busquedaEntregas, setBusquedaEntregas] = useState('');

  // Filtros - Aprobaciones tab
  const [filtroEstadoAprob, setFiltroEstadoAprob] = useState('pendiente'); // 'pendiente' | 'aprobado' | 'rechazado' | 'todos'
  const [busquedaAprob, setBusquedaAprob] = useState('');

  const [guardando, setGuardando] = useState(false);

  // =================== DATA LOADING ===================
  const cargarResidentes = useCallback(async () => {
    try {
      const data = await client.query(`{ residentes { id nombre telefono } }`);
      setResidentes(data.residentes || []);
    } catch (err) { console.error(err); }
  }, []);

  const cargarProyectos = useCallback(async () => {
    try {
      const data = await client.query(`{ proyectos(incluir_especiales: true, incluir_posventa: true) { id proyecto_nombre nombre_cliente apartamento estado } }`);
      setProyectos(data.proyectos || []);
    } catch (err) { console.error(err); }
  }, []);

  // Carga los apartamentos del módulo de posventa para marcar (badge) y clasificar
  // qué proyectos cuentan como posventa al relacionar un comprobante.
  const cargarPosventa = useCallback(async () => {
    try {
      const data = await client.query(`{ posventaSolicitudes { proyecto_id proyecto_nombre apartamento } }`);
      const ids = new Set(), keys = new Set(), aptosMap = new Map();
      for (const s of (data.posventaSolicitudes || [])) {
        if (s.proyecto_id != null) ids.add(s.proyecto_id);
        const key = `${normPv(s.proyecto_nombre)}|${normPv(s.apartamento)}`;
        keys.add(key);
        if (!aptosMap.has(key)) aptosMap.set(key, { proyecto_nombre: (s.proyecto_nombre || '').trim(), apartamento: (s.apartamento || '').trim() });
      }
      setPosventaSet({ ids, keys });
      setPosventaAptos(Array.from(aptosMap.values()));
    } catch (err) { console.error(err); }
  }, []);

  // ¿El proyecto está en el módulo de posventa? (badge + clasificación).
  const esProyectoPosventa = useCallback((p) => {
    if (!p) return false;
    return posventaSet.ids.has(p.id) || posventaSet.keys.has(`${normPv(p.proyecto_nombre)}|${normPv(p.apartamento)}`);
  }, [posventaSet]);

  const cargarEntregas = useCallback(async (resId = null) => {
    try {
      const vars = resId ? { ingeniero_id: resId } : {};
      const data = await client.query(`query($ingeniero_id: Int) { cajaMenorEntregasV2(ingeniero_id: $ingeniero_id) { entregas { id ingeniero_id ingeniero_nombre descripcion valor fecha proyectos { proyecto_id proyecto_nombre valor_asignado } total_comprobantes total_sin_aprobar saldo } resumen_ingenieros { ingeniero_id nombre total_entregado total_gastado total_sin_aprobar saldo } } }`, vars);
      setEntregas(data.cajaMenorEntregasV2.entregas || []);
      if (data.cajaMenorEntregasV2.resumen_ingenieros) setResumenResidentes(data.cajaMenorEntregasV2.resumen_ingenieros);
    } catch (err) { console.error(err); }
  }, []);

  const cargarComprobantes = useCallback(async () => {
    try {
      const data = await client.query(`{ cajaMenorComprobantesV2 { comprobantes { id ingeniero_id ingeniero_nombre entrega_id descripcion valor fecha estado es_administrativo archivos { id nombre_archivo ruta_archivo tipo_archivo } proyectos_distribucion { proyecto_id proyecto_nombre apartamento valor_distribuido } } conteos } }`);
      setComprobantes(data.cajaMenorComprobantesV2.comprobantes || []);
      if (data.cajaMenorComprobantesV2.conteos) setConteos(data.cajaMenorComprobantesV2.conteos);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    const cargarTodo = async () => {
      setLoading(true);
      await Promise.all([cargarResidentes(), cargarProyectos(), cargarEntregas(), cargarComprobantes(), cargarPosventa()]);
      setLoading(false);
    };
    cargarTodo();
  }, [cargarResidentes, cargarProyectos, cargarEntregas, cargarComprobantes, cargarPosventa]);

  // =================== ENTREGAS ===================
  const resetFormEntrega = () => {
    setFormEntrega({ residente_id: '', fecha: new Date().toISOString().split('T')[0], descripcion: '', valor: '' });
    setMostrarFormEntrega(false);
  };

  const guardarEntrega = async () => {
    if (!formEntrega.residente_id) { error('Selecciona un residente'); return; }
    const valor = parseFloat(formEntrega.valor);
    if (!valor || valor <= 0) { error('El valor debe ser mayor a 0'); return; }
    try {
      setGuardando(true);
      const body = {
        ingeniero_id: parseInt(formEntrega.residente_id),
        fecha: formEntrega.fecha,
        descripcion: formEntrega.descripcion,
        valor: valor
      };
      const gqlR = await client.mutate(`mutation($input: EntregaCajaMenorInput!) { crearEntregaCajaMenor(input: $input) { success message } }`, { input: { ingeniero_id: body.ingeniero_id, fecha: body.fecha, descripcion: body.descripcion, valor: body.valor } });
      if (gqlR.crearEntregaCajaMenor.success) {
        success('Entrega registrada');
        resetFormEntrega();
        cargarEntregas(residenteSeleccionado);
      } else error(gqlR.crearEntregaCajaMenor.message);
    } catch (err) { error('Error al guardar'); }
    finally { setGuardando(false); }
  };

  const eliminarEntrega = async (id) => {
    if (!window.confirm('¿Eliminar esta entrega?')) return;
    try {
      const gqlR = await client.mutate(`mutation($id: Int!) { eliminarEntregaCajaMenor(id: $id) { success message } }`, { id });
      if (gqlR.eliminarEntregaCajaMenor.success) { success('Entrega eliminada'); cargarEntregas(residenteSeleccionado); }
    } catch (err) { error('Error al eliminar'); }
  };

  // =================== COMPROBANTES ===================
  const totalComprobante = useMemo(() => {
    if (formComprobante.es_administrativo) return parseFloat(formComprobante.valor_admin) || 0;
    return formComprobante.proyectos.reduce((s, p) => s + (parseFloat(p.valor) || 0), 0);
  }, [formComprobante.proyectos, formComprobante.es_administrativo, formComprobante.valor_admin]);

  const seleccionarEntregaComp = (entregaId) => {
    setFormComprobante(prev => ({ ...prev, entrega_id: entregaId, proyectos: [] }));
  };

  const toggleProyectoComprobante = (proyId) => {
    const exists = formComprobante.proyectos.find(p => p.proyecto_id === proyId);
    if (exists) {
      setFormComprobante({ ...formComprobante, proyectos: formComprobante.proyectos.filter(p => p.proyecto_id !== proyId) });
    } else {
      setFormComprobante({ ...formComprobante, proyectos: [...formComprobante.proyectos, { proyecto_id: proyId, valor: '', asignado: 0, gastado: 0, saldo: 0 }] });
    }
  };

  // Selecciona un apartamento del módulo de posventa que aún no existe como proyecto:
  // resuelve/crea su entrada posventa separada (nunca un proyecto de obra real) y la agrega.
  const seleccionarPosventaApto = async (apto) => {
    try {
      const r = await client.mutate(
        `mutation($n: String!, $a: String!) { resolverAptoPosventa(proyecto_nombre: $n, apartamento: $a) { success proyecto_id message } }`,
        { n: apto.proyecto_nombre, a: apto.apartamento });
      const res = r.resolverAptoPosventa;
      if (!res?.success || !res.proyecto_id) { error(res?.message || 'No se pudo resolver el apartamento posventa'); return; }
      setProyectos(prev => prev.some(p => p.id === res.proyecto_id)
        ? prev
        : [...prev, { id: res.proyecto_id, proyecto_nombre: apto.proyecto_nombre, apartamento: apto.apartamento, nombre_cliente: '', estado: 'posventa' }]);
      setFormComprobante(prev => prev.proyectos.some(fp => fp.proyecto_id === res.proyecto_id)
        ? prev
        : { ...prev, proyectos: [...prev.proyectos, { proyecto_id: res.proyecto_id, valor: '', asignado: 0, gastado: 0, saldo: 0 }] });
      setBusquedaProyectoComp('');
    } catch { error('Error al seleccionar el apartamento posventa'); }
  };

  const actualizarValorProyectoComp = (proyId, valor) => {
    setFormComprobante({
      ...formComprobante,
      proyectos: formComprobante.proyectos.map(p => p.proyecto_id === proyId ? { ...p, valor } : p)
    });
  };

  const guardarComprobante = async () => {
    if (!formComprobante.residente_id) { error('Selecciona un residente'); return; }
    if (!formComprobante.descripcion.trim()) { error('La descripción es obligatoria'); return; }
    const esAdmin = !!formComprobante.es_administrativo;
    if (esAdmin) {
      const v = parseFloat(formComprobante.valor_admin) || 0;
      if (v <= 0) { error('El valor administrativo debe ser mayor a 0'); return; }
    } else {
      if (formComprobante.proyectos.length === 0) { error('Selecciona al menos un proyecto o marca como Administrativo'); return; }
      if (totalComprobante <= 0) { error('El valor total debe ser mayor a 0'); return; }
      const proyectosConValor = formComprobante.proyectos.filter(p => parseFloat(p.valor) > 0);
      if (proyectosConValor.length === 0) { error('Al menos un proyecto debe tener valor'); return; }
    }
    if (archivosComprobante.length === 0) { error('Debes adjuntar al menos un archivo'); return; }
    const proyectosConValor = esAdmin ? [] : formComprobante.proyectos.filter(p => parseFloat(p.valor) > 0);
    try {
      setGuardando(true);
      const gqlR = await client.mutate(`mutation($input: ComprobanteCajaMenorInput!) { crearComprobanteCajaMenor(input: $input) { success message comprobante_id } }`, {
        input: {
          ingeniero_id: parseInt(formComprobante.residente_id),
          entrega_id: null,
          descripcion: formComprobante.descripcion,
          fecha: formComprobante.fecha,
          es_administrativo: esAdmin,
          valor_administrativo: esAdmin ? (parseFloat(formComprobante.valor_admin) || 0) : null,
          proyectos: proyectosConValor.map(p => ({ proyecto_id: p.proyecto_id, valor: parseFloat(p.valor) }))
        }
      });
      const data = gqlR.crearComprobanteCajaMenor;
      if (data.success) {
        if (archivosComprobante.length > 0) {
          const { UPLOAD_URL } = await import('../../config');
          const token = localStorage.getItem('tuvi_token');
          for (const archivo of archivosComprobante) {
            const fd = new FormData();
            fd.append('comprobante_id', data.comprobante_id);
            fd.append('archivo', archivo);
            await fetch(`${UPLOAD_URL}/comprobante`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
          }
        }
        success('Comprobante creado');
        setMostrarFormComprobante(false);
        setFormComprobante({ residente_id: '', entrega_id: '', descripcion: '', fecha: new Date().toISOString().split('T')[0], proyectos: [], es_administrativo: false, valor_admin: '' });
        setArchivosComprobante([]);
        setBusquedaProyectoComp('');
        cargarComprobantes();
        cargarEntregas(residenteSeleccionado);
      } else error(data.message);
    } catch (err) { console.error('Error comprobante:', err); error('Error al guardar: ' + (err.message || '')); }
    finally { setGuardando(false); }
  };

  const eliminarComprobante = async (id) => {
    if (!window.confirm('¿Eliminar este comprobante?')) return;
    try {
      const gqlR = await client.mutate(`mutation($id: Int!) { eliminarComprobanteCajaMenor(id: $id) { success message } }`, { id });
      if (gqlR.eliminarComprobanteCajaMenor.success) { success('Comprobante eliminado'); cargarComprobantes(); cargarEntregas(residenteSeleccionado); }
    } catch (err) { error('Error al eliminar'); }
  };

  // =================== APROBACIONES ===================
  const iniciarAprobacion = (comp) => {
    setAprobandoId(comp.id);
    // Para gastos administrativos: no se necesita distribución a proyectos.
    if (comp.es_administrativo) {
      setDistribucion([]);
      return;
    }
    if (comp.proyectos_distribucion && comp.proyectos_distribucion.length > 0) {
      setDistribucion(comp.proyectos_distribucion.map(pd => ({
        proyecto_id: pd.proyecto_id, proyecto_nombre: pd.proyecto_nombre, valor: pd.valor_distribuido
      })));
      return;
    }
    if (comp.entrega_id) {
      const entrega = entregas.find(e => e.id === comp.entrega_id);
      if (entrega && entrega.proyectos.length > 0) {
        const valorPorProy = comp.valor / entrega.proyectos.length;
        setDistribucion(entrega.proyectos.map(p => ({
          proyecto_id: p.proyecto_id, proyecto_nombre: p.proyecto_nombre,
          valor: Math.round(valorPorProy * 100) / 100
        })));
        return;
      }
    }
    setDistribucion([{ proyecto_id: '', proyecto_nombre: '', valor: comp.valor }]);
  };

  const agregarProyectoDistribucion = () => setDistribucion([...distribucion, { proyecto_id: '', proyecto_nombre: '', valor: 0 }]);

  const actualizarDistribucion = (idx, campo, valor) => {
    const nueva = [...distribucion];
    if (campo === 'proyecto_id') {
      nueva[idx].proyecto_id = valor;
      const proy = proyectos.find(p => p.id === parseInt(valor));
      nueva[idx].proyecto_nombre = proy ? proy.proyecto_nombre : '';
    } else {
      nueva[idx].valor = parseFloat(valor) || 0;
    }
    setDistribucion(nueva);
  };

  const quitarProyectoDistribucion = (idx) => setDistribucion(distribucion.filter((_, i) => i !== idx));

  const aprobarComprobante = async (compId) => {
    const comp = comprobantes.find(c => c.id === compId);
    if (!comp) return;

    // Para gastos administrativos: aprobar sin distribución obligatoria
    if (comp.es_administrativo) {
      try {
        setGuardando(true);
        const gqlR = await client.mutate(`mutation($comprobante_id: Int!, $distribucion: [DistribucionInput!]!) { aprobarComprobanteCajaMenor(comprobante_id: $comprobante_id, distribucion: $distribucion) { success message } }`, {
          comprobante_id: compId, distribucion: []
        });
        if (gqlR.aprobarComprobanteCajaMenor.success) {
          success('Gasto administrativo aprobado');
          setAprobandoId(null); setDistribucion([]);
          cargarComprobantes(); cargarEntregas();
        } else error(gqlR.aprobarComprobanteCajaMenor.message);
      } catch (err) { error('Error al aprobar'); }
      finally { setGuardando(false); }
      return;
    }

    const totalDist = distribucion.reduce((s, d) => s + d.valor, 0);
    if (Math.abs(totalDist - comp.valor) > 0.01) {
      error(`La distribución (${formatMoney(totalDist)}) debe ser igual al valor (${formatMoney(comp.valor)})`);
      return;
    }
    if (distribucion.some(d => !d.proyecto_id)) { error('Todos los proyectos deben estar seleccionados'); return; }
    try {
      setGuardando(true);
      const gqlR = await client.mutate(`mutation($comprobante_id: Int!, $distribucion: [DistribucionInput!]!) { aprobarComprobanteCajaMenor(comprobante_id: $comprobante_id, distribucion: $distribucion) { success message } }`, {
        comprobante_id: compId, distribucion: distribucion.map(d => ({ proyecto_id: parseInt(d.proyecto_id), valor: d.valor }))
      });
      if (gqlR.aprobarComprobanteCajaMenor.success) {
        success('Comprobante aprobado y distribuido');
        setAprobandoId(null); setDistribucion([]);
        cargarComprobantes(); cargarEntregas();
      } else error(gqlR.aprobarComprobanteCajaMenor.message);
    } catch (err) { error('Error al aprobar'); }
    finally { setGuardando(false); }
  };

  const rechazarComprobante = async (compId) => {
    if (!window.confirm('¿Rechazar este comprobante?')) return;
    try {
      const gqlR = await client.mutate(`mutation($comprobante_id: Int!) { rechazarComprobanteCajaMenor(comprobante_id: $comprobante_id) { success message } }`, { comprobante_id: compId });
      if (gqlR.rechazarComprobanteCajaMenor.success) { success('Comprobante rechazado'); cargarComprobantes(); cargarEntregas(); }
    } catch (err) { error('Error al rechazar'); }
  };

  // =================== HELPERS ===================
  const formatMoney = (val) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(val || 0);

  const formatFecha = (f) => {
    if (!f) return '—';
    const d = f.includes(' ') ? new Date(f.replace(' ', 'T')) : f.includes('T') ? new Date(f) : new Date(f + 'T12:00:00');
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getNombreProyecto = (proyId) => {
    const p = proyectos.find(x => x.id === proyId);
    if (!p) return `Proyecto #${proyId}`;
    return `${p.proyecto_nombre}${p.apartamento ? ` · Apt ${p.apartamento}` : ''} - ${p.nombre_cliente}`;
  };

  const filtrarProyectos = (term, seleccionados) => {
    // Se puede buscar CUALQUIER proyecto por nombre, apartamento o cliente
    // (incluye entregados/posventa). Sin término solo se muestran los ya
    // seleccionados. Los proyectos entregados se marcan con badge "Posventa".
    return proyectos.filter(p => {
      const isSelected = seleccionados.some(s => s.proyecto_id === p.id);
      if (!term.trim()) return isSelected;
      const t = term.toLowerCase();
      return (p.proyecto_nombre || '').toLowerCase().includes(t) ||
             (p.nombre_cliente || '').toLowerCase().includes(t) ||
             (p.apartamento || '').toLowerCase().includes(t);
    });
  };

  // =================== FILTERED DATA ===================
  // Entregas filtradas por ingeniero + estado + búsqueda
  const entregasFiltradas = useMemo(() => {
    let lista = residenteSeleccionado ? entregas.filter(e => e.ingeniero_id === residenteSeleccionado) : entregas;

    // Filtro por estado
    const pctGas = (e) => e.valor > 0 ? (e.total_comprobantes / e.valor) * 100 : 0;
    if (filtroEstadoEntrega === 'activas') lista = lista.filter(e => pctGas(e) < 90);
    else if (filtroEstadoEntrega === 'agotadas') lista = lista.filter(e => pctGas(e) >= 90 && e.saldo >= 0);
    else if (filtroEstadoEntrega === 'excedidas') lista = lista.filter(e => e.saldo < 0);

    // Búsqueda
    if (busquedaEntregas.trim()) {
      const t = busquedaEntregas.toLowerCase();
      lista = lista.filter(e =>
        (e.ingeniero_nombre || '').toLowerCase().includes(t) ||
        (e.descripcion || '').toLowerCase().includes(t) ||
        e.proyectos.some(p => (p.proyecto_nombre || '').toLowerCase().includes(t) || (p.nombre_cliente || '').toLowerCase().includes(t))
      );
    }
    return lista;
  }, [entregas, residenteSeleccionado, filtroEstadoEntrega, busquedaEntregas]);

  // Conteo por estado para los filtros
  const conteosEntregas = useMemo(() => {
    const base = residenteSeleccionado ? entregas.filter(e => e.ingeniero_id === residenteSeleccionado) : entregas;
    const pctG = (e) => e.valor > 0 ? (e.total_comprobantes / e.valor) * 100 : 0;
    return {
      todas: base.length,
      activas: base.filter(e => pctG(e) < 90).length,
      agotadas: base.filter(e => pctG(e) >= 90 && e.saldo >= 0).length,
      excedidas: base.filter(e => e.saldo < 0).length
    };
  }, [entregas, residenteSeleccionado]);

  // Agrupación por residente
  const entregasPorResidente = useMemo(() => {
    const grupos = {};
    entregasFiltradas.forEach(e => {
      if (!grupos[e.ingeniero_id]) grupos[e.ingeniero_id] = { nombre: e.ingeniero_nombre, entregas: [] };
      grupos[e.ingeniero_id].entregas.push(e);
    });
    return Object.entries(grupos).sort((a, b) => a[1].nombre.localeCompare(b[1].nombre));
  }, [entregasFiltradas]);

  // Agrupación por proyecto basada en comprobantes aprobados
  const gastosPorProyecto = useMemo(() => {
    const grupos = {};
    const aprobados = comprobantes.filter(c => c.estado === 'aprobado');
    aprobados.forEach(c => {
      if (c.proyectos_distribucion) {
        c.proyectos_distribucion.forEach(pd => {
          const key = pd.proyecto_id;
          if (!grupos[key]) grupos[key] = { nombre: pd.proyecto_nombre || getNombreProyecto(pd.proyecto_id), detalles: [], total: 0 };
          grupos[key].detalles.push({
            residente: c.ingeniero_nombre,
            fecha: c.aprobado_at || c.fecha,
            descripcion: c.descripcion,
            valor: pd.valor_distribuido
          });
          grupos[key].total += pd.valor_distribuido;
        });
      }
    });
    return Object.entries(grupos).sort((a, b) => a[1].nombre.localeCompare(b[1].nombre));
  }, [comprobantes, proyectos]);

  // Comprobantes filtrados para aprobaciones
  const comprobantesFiltrados = useMemo(() => {
    let lista = comprobantes;
    if (filtroEstadoAprob !== 'todos') lista = lista.filter(c => c.estado === filtroEstadoAprob);
    if (busquedaAprob.trim()) {
      const t = busquedaAprob.toLowerCase();
      lista = lista.filter(c =>
        (c.ingeniero_nombre || '').toLowerCase().includes(t) ||
        (c.descripcion || '').toLowerCase().includes(t) ||
        (c.proyectos_distribucion || []).some(pd =>
          (pd.proyecto_nombre || '').toLowerCase().includes(t) ||
          (pd.apartamento || '').toString().toLowerCase().includes(t)
        )
      );
    }
    return lista;
  }, [comprobantes, filtroEstadoAprob, busquedaAprob]);

  // Comprobantes filtrados para tab Comprobantes
  const comprobantesTabFiltrados = useMemo(() => {
    let lista = comprobantes;
    if (filtroEstadoComp !== 'todos') lista = lista.filter(c => c.estado === filtroEstadoComp);
    if (busquedaComp.trim()) {
      const t = busquedaComp.toLowerCase();
      lista = lista.filter(c =>
        (c.ingeniero_nombre || '').toLowerCase().includes(t) ||
        (c.descripcion || '').toLowerCase().includes(t) ||
        (c.proyectos_distribucion || []).some(pd =>
          (pd.proyecto_nombre || '').toLowerCase().includes(t) ||
          (pd.apartamento || '').toString().toLowerCase().includes(t)
        )
      );
    }
    if (fechaDesdeComp) lista = lista.filter(c => c.fecha >= fechaDesdeComp);
    if (fechaHastaComp) lista = lista.filter(c => c.fecha <= fechaHastaComp);
    return lista;
  }, [comprobantes, filtroEstadoComp, busquedaComp, fechaDesdeComp, fechaHastaComp]);

  // ============ Exportar comprobantes (Aprobaciones) a Excel ============
  const exportarAprobacionesExcel = useCallback(() => {
    if (!comprobantesFiltrados || comprobantesFiltrados.length === 0) {
      error('No hay comprobantes para exportar con los filtros actuales');
      return;
    }

    // Si el estado actual es "todos", una sola hoja con todo.
    // Si es estado específico, una sola hoja con ese estado.
    const tituloEstado = filtroEstadoAprob === 'todos'
      ? 'Todos los estados'
      : filtroEstadoAprob.charAt(0).toUpperCase() + filtroEstadoAprob.slice(1) + 's';

    // Construir filas planas: una fila por DISTRIBUCION (un comprobante con
    // 3 proyectos genera 3 filas). Si el comprobante no tiene distribucion,
    // se genera 1 fila con campos de proyecto vacios.
    const filas = [];
    for (const c of comprobantesFiltrados) {
      const dist = c.proyectos_distribucion && c.proyectos_distribucion.length > 0
        ? c.proyectos_distribucion
        : [{ proyecto_nombre: '', apartamento: '', valor_distribuido: c.valor }];
      for (const pd of dist) {
        filas.push({
          id: c.id,
          fecha: c.fecha || '',
          residente: c.ingeniero_nombre || '',
          descripcion: c.descripcion || '',
          estado: (c.estado || '').toUpperCase(),
          es_admin: c.es_administrativo ? 'Sí' : 'No',
          proyecto: pd.proyecto_nombre || '',
          apto: pd.apartamento || '',
          valor_dist: Number(pd.valor_distribuido) || 0,
          valor_total: Number(c.valor) || 0,
        });
      }
    }

    const columnas = [
      { header: '# Comp', key: 'id', width: 8 },
      { header: 'Fecha', key: 'fecha', width: 12 },
      { header: 'Residente', key: 'residente', width: 22 },
      { header: 'Descripcion', key: 'descripcion', width: 35 },
      { header: 'Estado', key: 'estado', width: 12 },
      { header: 'Admin.', key: 'es_admin', width: 8 },
      { header: 'Proyecto', key: 'proyecto', width: 26 },
      { header: 'Apto', key: 'apto', width: 10 },
      { header: 'Valor distribuido', key: 'valor_dist', width: 18, money: true },
      { header: 'Valor comp. total', key: 'valor_total', width: 18, money: true },
    ];

    const aoa = [];
    // Titulo
    aoa.push([`Caja Menor - Comprobantes (${tituloEstado})`]);
    aoa.push([`Generado: ${new Date().toLocaleString('es-CO', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })} - ${comprobantesFiltrados.length} comprobantes`]);
    aoa.push([]);
    // Headers
    aoa.push(columnas.map(c => c.header));
    // Data
    filas.forEach(f => aoa.push(columnas.map(c => f[c.key])));
    // Totales (sumar UNICOS por id para valor_total, y todos para valor_dist)
    const totalDist = filas.reduce((s, f) => s + (f.valor_dist || 0), 0);
    const idsUnicos = new Set();
    const totalComp = filas.reduce((s, f) => {
      if (idsUnicos.has(f.id)) return s;
      idsUnicos.add(f.id);
      return s + (f.valor_total || 0);
    }, 0);
    aoa.push([]);
    aoa.push([`TOTAL (${idsUnicos.size} comprobantes únicos)`, '', '', '', '', '', '', '', totalDist, totalComp]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = columnas.map(c => ({ wch: c.width }));
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: columnas.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: columnas.length - 1 } },
    ];

    // Estilo titulo
    const titleCell = XLSX.utils.encode_cell({ r: 0, c: 0 });
    if (ws[titleCell]) ws[titleCell].s = {
      font: { bold: true, sz: 14, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '1C2837' } },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
    const subCell = XLSX.utils.encode_cell({ r: 1, c: 0 });
    if (ws[subCell]) ws[subCell].s = {
      font: { italic: true, sz: 10, color: { rgb: '475569' } },
      alignment: { horizontal: 'center' },
    };

    // Estilo headers (fila 3, 0-indexed)
    for (let i = 0; i < columnas.length; i++) {
      const ref = XLSX.utils.encode_cell({ r: 3, c: i });
      if (!ws[ref]) ws[ref] = { v: columnas[i].header, t: 's' };
      ws[ref].s = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '3B82F6' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } },
        },
      };
    }

    // Colorear filas por estado + formato moneda en columnas de dinero
    const estadoColors = {
      'PENDIENTE': { fg: 'FEF3C7', txt: '92400E' }, // amarillo
      'APROBADO':  { fg: 'DCFCE7', txt: '166534' }, // verde
      'RECHAZADO': { fg: 'FEE2E2', txt: '991B1B' }, // rojo
    };
    for (let r = 4; r < 4 + filas.length; r++) {
      const fila = filas[r - 4];
      const color = estadoColors[fila.estado];
      for (let i = 0; i < columnas.length; i++) {
        const ref = XLSX.utils.encode_cell({ r, c: i });
        if (!ws[ref]) continue;
        const col = columnas[i];
        const style = {
          border: { bottom: { style: 'thin', color: { rgb: 'E5E7EB' } } },
        };
        // Fondo para columna Estado
        if (col.key === 'estado' && color) {
          style.fill = { fgColor: { rgb: color.fg } };
          style.font = { bold: true, color: { rgb: color.txt } };
          style.alignment = { horizontal: 'center' };
        }
        // Formato moneda
        if (col.money && typeof ws[ref].v === 'number') {
          ws[ref].z = '"$ "#,##0';
          style.alignment = { horizontal: 'right' };
        }
        ws[ref].s = style;
      }
    }

    // Estilo fila TOTALES (ultima fila no vacia)
    const filaTotal = 4 + filas.length + 1;
    for (let i = 0; i < columnas.length; i++) {
      const ref = XLSX.utils.encode_cell({ r: filaTotal, c: i });
      if (!ws[ref]) continue;
      ws[ref].s = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '1C2837' } },
        alignment: { horizontal: columnas[i].money ? 'right' : 'left' },
      };
      if (columnas[i].money && typeof ws[ref].v === 'number') ws[ref].z = '"$ "#,##0';
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Comprobantes');

    // Si exportamos TODOS, agregamos hojas separadas por estado.
    if (filtroEstadoAprob === 'todos') {
      ['pendiente', 'aprobado', 'rechazado'].forEach(est => {
        const subset = comprobantesFiltrados.filter(c => c.estado === est);
        if (subset.length === 0) return;
        const subFilas = [];
        for (const c of subset) {
          const dist = c.proyectos_distribucion && c.proyectos_distribucion.length > 0
            ? c.proyectos_distribucion
            : [{ proyecto_nombre: '', apartamento: '', valor_distribuido: c.valor }];
          for (const pd of dist) {
            subFilas.push([
              c.id, c.fecha || '', c.ingeniero_nombre || '', c.descripcion || '',
              c.es_administrativo ? 'Sí' : 'No',
              pd.proyecto_nombre || '', pd.apartamento || '',
              Number(pd.valor_distribuido) || 0, Number(c.valor) || 0,
            ]);
          }
        }
        const subAoa = [
          [`Comprobantes - ${est.charAt(0).toUpperCase() + est.slice(1)}s (${subset.length})`],
          [],
          ['# Comp', 'Fecha', 'Residente', 'Descripcion', 'Admin.', 'Proyecto', 'Apto', 'Valor distribuido', 'Valor comp. total'],
          ...subFilas,
        ];
        const ws2 = XLSX.utils.aoa_to_sheet(subAoa);
        ws2['!cols'] = [8, 12, 22, 35, 8, 26, 10, 18, 18].map(w => ({ wch: w }));
        ws2['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 8 } }];
        // Titulo
        const t = XLSX.utils.encode_cell({ r: 0, c: 0 });
        const color = estadoColors[est.toUpperCase()];
        if (ws2[t]) ws2[t].s = {
          font: { bold: true, sz: 13, color: { rgb: color?.txt || 'FFFFFF' } },
          fill: { fgColor: { rgb: color?.fg || '1C2837' } },
          alignment: { horizontal: 'center' },
        };
        // Headers
        for (let i = 0; i < 9; i++) {
          const ref = XLSX.utils.encode_cell({ r: 2, c: i });
          if (ws2[ref]) ws2[ref].s = {
            font: { bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '3B82F6' } },
            alignment: { horizontal: 'center' },
          };
        }
        // Formato moneda cols 7 y 8
        for (let r = 3; r < 3 + subFilas.length; r++) {
          [7, 8].forEach(c => {
            const ref = XLSX.utils.encode_cell({ r, c });
            if (ws2[ref] && typeof ws2[ref].v === 'number') {
              ws2[ref].z = '"$ "#,##0';
              ws2[ref].s = { alignment: { horizontal: 'right' } };
            }
          });
        }
        XLSX.utils.book_append_sheet(wb, ws2, est.charAt(0).toUpperCase() + est.slice(1) + 's');
      });
    }

    const fechaArchivo = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `CajaMenor_Aprobaciones_${tituloEstado.replace(/\s+/g, '_')}_${fechaArchivo}.xlsx`);
    success('Excel descargado');
  }, [comprobantesFiltrados, filtroEstadoAprob, success, error]);

  // Entregas del residente seleccionado en form comprobante
  const entregasDelResidente = formComprobante.residente_id
    ? entregas.filter(e => e.ingeniero_id === parseInt(formComprobante.residente_id))
    : [];

  // SVG icons
  const IconX = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;

  if (loading) {
    return <div className="cm-container"><div className="cm-loading"><div className="cm-spinner"></div><p>Cargando...</p></div></div>;
  }

  // =================== RENDER ENTREGA ITEM (reusable) ===================
  const renderEntregaItem = (e) => {
    const pctGastado = e.valor > 0 ? Math.round((e.total_comprobantes / e.valor) * 100) : 0;
    const alertaAlta = pctGastado >= 90;
    return (
      <div key={e.id} className={`cm-entrega-item ${alertaAlta ? 'cm-alerta-alta' : ''}`}>
        <div className="cm-entrega-info">
          <div className="cm-entrega-header">
            {e.ingeniero_nombre && <span className="cm-entrega-nombre"><strong>{e.ingeniero_nombre}</strong></span>}
            <span className="cm-entrega-fecha">{formatFecha(e.fecha)}</span>
            {alertaAlta && (
              <span className="cm-alerta-badge">
                {pctGastado >= 100 ? 'AGOTADO' : `${pctGastado}% usado`}
              </span>
            )}
          </div>
          {e.descripcion && <p className="cm-entrega-desc">{e.descripcion}</p>}
        </div>
        <div className="cm-entrega-valores">
          <div className="cm-val-entregado">{formatMoney(e.valor)}</div>
        </div>
        <div className="cm-entrega-acciones">
          {permisoEliminarEntrega && (
            <button className="cm-btn-icon cm-btn-danger" onClick={() => eliminarEntrega(e.id)} title="Eliminar"><IconX /></button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="cm-container">
      <div className="cm-header">
        <h2 className="cm-title">Caja Menor</h2>
      </div>

      {/* Tabs */}
      <div className="cm-tabs">
        {permisoIngenieros && <button className={`cm-tab ${tabActiva === 'residentes' ? 'activa' : ''}`} onClick={() => setTabActiva('residentes')}>Residentes</button>}
        {permisoComprobantes && <button className={`cm-tab ${tabActiva === 'comprobantes' ? 'activa' : ''}`} onClick={() => setTabActiva('comprobantes')}>Comprobantes</button>}
        {permisoAprobaciones && (
          <button className={`cm-tab ${tabActiva === 'aprobaciones' ? 'activa' : ''}`} onClick={() => setTabActiva('aprobaciones')}>
            Aprobaciones
            {conteos.pendiente > 0 && <span className="cm-tab-badge">{conteos.pendiente}</span>}
          </button>
        )}
      </div>

      {/* =================== TAB INGENIEROS =================== */}
      {/* Permiso requerido: caja-menor-ingenieros */}
      {tabActiva === 'residentes' && permisoIngenieros && (
        <div className="cm-tab-content">
          {/* Resumen por residente con saldo destacado */}
          <div className="cm-resumen-grid">
            {resumenResidentes.map(r => {
              const saldo = Number(r.saldo) || 0;
              const sinAprobar = Number(r.total_sin_aprobar) || 0;
              const debe = saldo < 0;
              const aFavor = saldo > 0;
              const enCero = saldo === 0;
              return (
                <div key={r.ingeniero_id}
                  className={`cm-ingeniero-card ${residenteSeleccionado === r.ingeniero_id ? 'seleccionado' : ''} ${debe ? 'cm-card-debe' : aFavor ? 'cm-card-favor' : ''}`}
                  onClick={() => { const s = residenteSeleccionado === r.ingeniero_id ? null : r.ingeniero_id; setResidenteSeleccionado(s); cargarEntregas(s); }}>
                  <div className="cm-ing-nombre">{r.nombre}</div>
                  <div className="cm-ing-stats">
                    <div className="cm-ing-stat"><span className="cm-ing-label">Entregado</span><span className="cm-ing-valor">{formatMoney(r.total_entregado)}</span></div>
                    <div className="cm-ing-stat"><span className="cm-ing-label">Gastado (aprobado)</span><span className="cm-ing-valor cm-gastado">{formatMoney(r.total_gastado)}</span></div>
                  </div>
                  <div className="cm-saldo-banner">
                    {debe && (
                      <>
                        <span className="cm-saldo-icono">⚠️</span>
                        <span className="cm-saldo-texto">Debe</span>
                        <span className="cm-saldo-monto cm-negativo">{formatMoney(Math.abs(saldo))}</span>
                      </>
                    )}
                    {aFavor && (
                      <>
                        <span className="cm-saldo-icono">✅</span>
                        <span className="cm-saldo-texto">A favor</span>
                        <span className="cm-saldo-monto cm-positivo">{formatMoney(saldo)}</span>
                      </>
                    )}
                    {enCero && (
                      <>
                        <span className="cm-saldo-icono">⚖️</span>
                        <span className="cm-saldo-texto">Saldo en cero</span>
                      </>
                    )}
                  </div>
                  {sinAprobar > 0 && (
                    <div className="cm-saldo-banner cm-saldo-sinaprobar" title="Comprobantes registrados esperando aprobación">
                      <span className="cm-saldo-icono">🕒</span>
                      <span className="cm-saldo-texto">Sin aprobar</span>
                      <span className="cm-saldo-monto cm-pendiente">{formatMoney(sinAprobar)}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {resumenResidentes.length === 0 && <div className="cm-empty">Registra residentes desde Administrativo &gt; Residentes</div>}

          {/* Toolbar: título + vista toggle + filtros + búsqueda + botón */}
          <div className="cm-toolbar">
            <div className="cm-toolbar-left">
              <h3 className="cm-toolbar-title">
                {residenteSeleccionado
                  ? <>Entregas de {resumenResidentes.find(r => r.ingeniero_id === residenteSeleccionado)?.nombre || residentes.find(r => r.id === residenteSeleccionado)?.nombre || ''}
                      <button className="cm-btn cm-btn-sm cm-btn-secondary" style={{ marginLeft: 8 }} onClick={() => { setResidenteSeleccionado(null); cargarEntregas(null); }}>Ver todos</button>
                    </>
                  : 'Todas las entregas'}
              </h3>
              <div className="cm-vista-toggle">
                <button className={`cm-vista-btn ${vistaEntregas === 'residente' ? 'activo' : ''}`} onClick={() => setVistaEntregas('residente')}>Por Residente</button>
                <button className={`cm-vista-btn ${vistaEntregas === 'proyecto' ? 'activo' : ''}`} onClick={() => setVistaEntregas('proyecto')}>Por Proyecto</button>
              </div>
            </div>
            {permisoCrearEntrega && (
              <button className="cm-btn cm-btn-primary" onClick={() => { resetFormEntrega(); setMostrarFormEntrega(true); }}>+ Nueva Entrega</button>
            )}
          </div>

          {/* Filtros de estado + búsqueda */}
          <div className="cm-filtros-bar">
            <div className="cm-filtros-estados">
              {[
                { key: 'todas', label: 'Todas', count: conteosEntregas.todas },
                { key: 'activas', label: 'Activas', count: conteosEntregas.activas },
                { key: 'agotadas', label: 'Agotadas', count: conteosEntregas.agotadas },
                { key: 'excedidas', label: 'Excedidas', count: conteosEntregas.excedidas }
              ].map(f => (
                <button key={f.key}
                  className={`cm-filtro-btn ${filtroEstadoEntrega === f.key ? 'activo' : ''} ${f.key === 'excedidas' && f.count > 0 ? 'cm-filtro-alerta' : ''}`}
                  onClick={() => setFiltroEstadoEntrega(f.key)}>
                  {f.label} <span className="cm-filtro-count">{f.count}</span>
                </button>
              ))}
            </div>
            <input
              type="text" value={busquedaEntregas} onChange={e => setBusquedaEntregas(e.target.value)}
              placeholder="Buscar por residente, proyecto o descripción..."
              className="cm-search-bar"
            />
          </div>

          {/* Form nueva entrega - Solo residente + valor (sin proyecto) */}
          {mostrarFormEntrega && (() => {
            // Calcular saldo previo del residente seleccionado y efectivo neto a entregar
            const resSel = resumenResidentes.find(r => r.ingeniero_id === parseInt(formEntrega.residente_id));
            const saldoPrevio = resSel ? Number(resSel.saldo) || 0 : 0;
            const valorEntrega = parseFloat(formEntrega.valor) || 0;
            // Si DEBE (saldo < 0): se cubre con la nueva entrega → efectivo neto = valor + saldo
            // Si tiene A FAVOR o cero: efectivo neto = valor entregado tal cual
            const efectivoNeto = saldoPrevio < 0 ? Math.max(0, valorEntrega + saldoPrevio) : valorEntrega;
            const saldoFinal = saldoPrevio + valorEntrega;
            return (
            <div className="cm-form-card">
              <h4>Registrar Entrega de Dinero</h4>
              <div className="cm-form-row">
                <div className="cm-form-group">
                  <label>Residente *</label>
                  <select value={formEntrega.residente_id} onChange={e => setFormEntrega({ ...formEntrega, residente_id: e.target.value })}>
                    <option value="">Seleccionar...</option>
                    {residentes.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                  </select>
                </div>
                <div className="cm-form-group">
                  <label>Valor *</label>
                  <input type="text" value={formEntrega.valor ? Number(formEntrega.valor).toLocaleString('es-CO') : ''}
                    onChange={e => {
                      const raw = e.target.value.replace(/[^0-9]/g, '');
                      setFormEntrega({ ...formEntrega, valor: raw });
                    }}
                    placeholder="Valor a entregar" />
                </div>
                <div className="cm-form-group">
                  <label>Fecha</label>
                  <input type="date" value={formEntrega.fecha} onChange={e => setFormEntrega({ ...formEntrega, fecha: e.target.value })} />
                </div>
              </div>
              <div className="cm-form-group">
                <label>Descripción</label>
                <input type="text" value={formEntrega.descripcion} onChange={e => setFormEntrega({ ...formEntrega, descripcion: e.target.value })} placeholder="Motivo de la entrega..." />
              </div>

              {/* Resumen de saldo y efectivo neto */}
              {resSel && (
                <div className="cm-saldo-resumen">
                  <div className="cm-saldo-row">
                    <span className="cm-saldo-label">Saldo previo de {resSel.nombre}:</span>
                    <span className={`cm-saldo-valor ${saldoPrevio < 0 ? 'cm-saldo-debe' : saldoPrevio > 0 ? 'cm-saldo-favor' : 'cm-saldo-cero'}`}>
                      {saldoPrevio < 0 && '⚠️ Debe '}
                      {saldoPrevio > 0 && '✅ A favor '}
                      {formatMoney(Math.abs(saldoPrevio))}
                    </span>
                  </div>
                  {valorEntrega > 0 && (
                    <>
                      <div className="cm-saldo-row">
                        <span className="cm-saldo-label">Valor a registrar:</span>
                        <span className="cm-saldo-valor">{formatMoney(valorEntrega)}</span>
                      </div>
                      {saldoPrevio < 0 && (
                        <div className="cm-saldo-row cm-saldo-destacado">
                          <span className="cm-saldo-label">💵 Efectivo neto a entregar (descontando deuda):</span>
                          <span className="cm-saldo-valor cm-saldo-neto">{formatMoney(efectivoNeto)}</span>
                        </div>
                      )}
                      <div className="cm-saldo-row cm-saldo-final">
                        <span className="cm-saldo-label">Nuevo saldo total disponible:</span>
                        <span className={`cm-saldo-valor ${saldoFinal < 0 ? 'cm-saldo-debe' : 'cm-saldo-favor'}`}>
                          {formatMoney(saldoFinal)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="cm-form-actions">
                <button className="cm-btn cm-btn-secondary" onClick={resetFormEntrega}>Cancelar</button>
                <button className="cm-btn cm-btn-primary" onClick={guardarEntrega} disabled={guardando}>
                  {guardando ? 'Guardando...' : 'Registrar Entrega'}
                </button>
              </div>
            </div>
            );
          })()}

          {/* ---- Vista por Residente ---- */}
          {vistaEntregas === 'residente' && (
            <div className="cm-entregas-agrupadas">
              {entregasPorResidente.length === 0 ? (
                <div className="cm-empty">No hay entregas {filtroEstadoEntrega !== 'todas' ? 'con este filtro' : 'registradas'}</div>
              ) : (
                entregasPorResidente.map(([resId, grupo]) => (
                  <div key={resId} className="cm-grupo-ingeniero">
                    <div className="cm-grupo-header">
                      <span className="cm-grupo-nombre">{grupo.nombre}</span>
                      <span className="cm-grupo-count">{grupo.entregas.length} entrega{grupo.entregas.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="cm-grupo-entregas">
                      {grupo.entregas.map(e => renderEntregaItem(e))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ---- Vista por Proyecto (totales de comprobantes aprobados) ---- */}
          {vistaEntregas === 'proyecto' && (
            <div className="cm-entregas-agrupadas">
              {gastosPorProyecto.length === 0 ? (
                <div className="cm-empty">No hay gastos aprobados por proyecto</div>
              ) : (
                gastosPorProyecto.map(([proyId, grupo]) => (
                  <div key={proyId} className="cm-grupo-ingeniero">
                    <div className="cm-grupo-header">
                      <div>
                        <span className="cm-grupo-nombre">{grupo.nombre}</span>
                      </div>
                      <div className="cm-grupo-totales">
                        <span className="cm-grupo-stat cm-rojo">Total gastado: {formatMoney(grupo.total)}</span>
                      </div>
                    </div>
                    <div className="cm-grupo-entregas">
                      {grupo.detalles.map((d, i) => (
                        <div key={i} className="cm-proy-detalle-item">
                          <span className="cm-proy-det-ing">{d.residente}</span>
                          <span className="cm-proy-det-fecha">{formatFecha(d.fecha)}</span>
                          {d.descripcion && <span className="cm-proy-det-desc">{d.descripcion}</span>}
                          <span className="cm-proy-det-valor cm-rojo">Gastado: {formatMoney(d.valor)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* =================== TAB COMPROBANTES =================== */}
      {tabActiva === 'comprobantes' && permisoComprobantes && (
        <div className="cm-tab-content">
          <div className="cm-section-header">
            <h3>Comprobantes de Gasto</h3>
            <button className="cm-btn cm-btn-primary" onClick={() => setMostrarFormComprobante(true)}>+ Nuevo Comprobante</button>
          </div>

          {mostrarFormComprobante && (() => {
            const resSelComp = resumenResidentes.find(r => r.ingeniero_id === parseInt(formComprobante.residente_id));
            const saldoResComp = resSelComp ? Number(resSelComp.saldo) || 0 : 0;
            return (
            <div className="cm-form-card">
              <h4>Registrar Comprobante</h4>
              {resSelComp && (
                <div className="cm-saldo-banner-inline" style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 14px', borderRadius: 8, marginBottom: 12,
                  background: saldoResComp < 0 ? '#fef2f2' : saldoResComp > 0 ? '#ecfdf5' : '#f1f5f9',
                  border: `1px solid ${saldoResComp < 0 ? '#fecaca' : saldoResComp > 0 ? '#a7f3d0' : '#cbd5e1'}`
                }}>
                  <span style={{ color: '#475569', fontWeight: 600 }}>
                    {saldoResComp < 0 && '⚠️ Saldo de '}{saldoResComp > 0 && '✅ Saldo de '}{saldoResComp === 0 && '⚖️ Saldo de '}
                    <strong>{resSelComp.nombre}</strong>:
                  </span>
                  <span style={{
                    fontWeight: 800, fontSize: '1.1rem',
                    color: saldoResComp < 0 ? '#dc2626' : saldoResComp > 0 ? '#059669' : '#64748b'
                  }}>
                    {saldoResComp < 0 ? `Debe ${formatMoney(Math.abs(saldoResComp))}` : saldoResComp > 0 ? `A favor ${formatMoney(saldoResComp)}` : formatMoney(0)}
                  </span>
                </div>
              )}
              <div className="cm-form-row">
                <div className="cm-form-group cm-form-grow">
                  <label>Residente *</label>
                  <select value={formComprobante.residente_id} onChange={e => setFormComprobante({ ...formComprobante, residente_id: e.target.value, entrega_id: '', proyectos: [] })}>
                    <option value="">Seleccionar...</option>
                    {residentes.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                  </select>
                </div>
              </div>
              <div className="cm-form-row">
                <div className="cm-form-group cm-form-grow">
                  <label>Descripción *</label>
                  <input type="text" value={formComprobante.descripcion} onChange={e => setFormComprobante({ ...formComprobante, descripcion: e.target.value })} placeholder="¿En qué se gastó?" />
                </div>
                <div className="cm-form-group">
                  <label>Valor Total</label>
                  <input type="text" value={formatMoney(totalComprobante)} readOnly className="cm-input-readonly" />
                </div>
                <div className="cm-form-group">
                  <label>Fecha</label>
                  <input type="date" value={formComprobante.fecha} onChange={e => setFormComprobante({ ...formComprobante, fecha: e.target.value })} />
                </div>
              </div>
              <div className="cm-form-group">
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 0 }}>
                  <input type="checkbox" style={{ width: 'auto', margin: 0, flexShrink: 0 }} checked={!!formComprobante.es_administrativo}
                    onChange={(e) => setFormComprobante({ ...formComprobante, es_administrativo: e.target.checked, proyectos: e.target.checked ? [] : formComprobante.proyectos })} />
                  <span>Gasto Administrativo (sin proyecto)</span>
                </label>
              </div>
              {formComprobante.es_administrativo ? (
                <div className="cm-form-group">
                  <label>Valor Administrativo *</label>
                  <input type="number" value={formComprobante.valor_admin}
                    onChange={(e) => setFormComprobante({ ...formComprobante, valor_admin: e.target.value })}
                    placeholder="0" className="cm-input" />
                  <div className="cm-proy-total" style={{ marginTop: 6 }}>Total: <strong>{formatMoney(totalComprobante)}</strong></div>
                </div>
              ) : (
                <div className="cm-form-group">
                  <label>Gasto por proyecto * {formComprobante.proyectos.length > 0 && <span className="cm-badge-sm">{formComprobante.proyectos.length} proyectos</span>}</label>
                  <input type="text" value={busquedaProyectoComp} onChange={e => setBusquedaProyectoComp(e.target.value)} placeholder="Buscar proyecto..." className="cm-search-input" />
                  {formComprobante.proyectos.length > 0 && (
                    <div className="cm-proy-valores">
                      {formComprobante.proyectos.map(fp => (
                        <div key={fp.proyecto_id} className="cm-proy-valor-row">
                          <span className="cm-proy-valor-nombre">{getNombreProyecto(fp.proyecto_id)}{esProyectoPosventa(proyectos.find(x => x.id === fp.proyecto_id)) && <span className="cm-badge-posventa">Posventa</span>}</span>
                          <input type="number" value={fp.valor} onChange={e => actualizarValorProyectoComp(fp.proyecto_id, e.target.value)} placeholder="Valor gastado" className="cm-proy-valor-input" />
                          <button className="cm-btn-icon cm-btn-danger" onClick={() => toggleProyectoComprobante(fp.proyecto_id)} title="Quitar"><IconX /></button>
                        </div>
                      ))}
                      <div className="cm-proy-total">Total: <strong>{formatMoney(totalComprobante)}</strong></div>
                    </div>
                  )}
                  <div className="cm-proyectos-check">
                    {filtrarProyectos(busquedaProyectoComp, formComprobante.proyectos)
                      .filter(p => !formComprobante.proyectos.some(fp => fp.proyecto_id === p.id))
                      .map(p => (
                      <label key={p.id} className="cm-check-label" onClick={() => toggleProyectoComprobante(p.id)}>
                        <span className="cm-check-add">+</span>
                        <span>{p.proyecto_nombre}{p.apartamento ? ` · Apt ${p.apartamento}` : ''} - {p.nombre_cliente}</span>
                        {esProyectoPosventa(p) && <span className="cm-badge-posventa">Posventa</span>}
                      </label>
                    ))}
                    {/* Apartamentos del módulo de posventa que aún no existen como proyecto */}
                    {(() => {
                      const t = busquedaProyectoComp.trim().toLowerCase();
                      if (!t) return null;
                      const existentes = new Set(proyectos.map(p => `${normPv(p.proyecto_nombre)}|${normPv(p.apartamento)}`));
                      const extra = posventaAptos.filter(a =>
                        !existentes.has(`${normPv(a.proyecto_nombre)}|${normPv(a.apartamento)}`) &&
                        ((a.proyecto_nombre || '').toLowerCase().includes(t) || (a.apartamento || '').toLowerCase().includes(t)));
                      return extra.map((a, i) => (
                        <label key={`pv-${normPv(a.proyecto_nombre)}-${normPv(a.apartamento)}-${i}`} className="cm-check-label" onClick={() => seleccionarPosventaApto(a)}>
                          <span className="cm-check-add">+</span>
                          <span>{a.proyecto_nombre}{a.apartamento ? ` · Apt ${a.apartamento}` : ''}</span>
                          <span className="cm-badge-posventa">Posventa</span>
                        </label>
                      ));
                    })()}
                  </div>
                </div>
              )}
              <div className="cm-form-group">
                <label>Archivos adjuntos</label>
                <input type="file" multiple onChange={e => setArchivosComprobante([...archivosComprobante, ...Array.from(e.target.files)])} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" />
                {archivosComprobante.length > 0 && (
                  <div className="cm-archivos-preview">
                    {archivosComprobante.map((f, i) => (
                      <span key={i} className="cm-archivo-tag">{f.name}<button onClick={() => setArchivosComprobante(archivosComprobante.filter((_, j) => j !== i))}>x</button></span>
                    ))}
                  </div>
                )}
              </div>
              <div className="cm-form-actions">
                <button className="cm-btn cm-btn-secondary" onClick={() => { setMostrarFormComprobante(false); setArchivosComprobante([]); setBusquedaProyectoComp(''); }}>Cancelar</button>
                <button className="cm-btn cm-btn-primary" onClick={guardarComprobante} disabled={guardando}>
                  {guardando ? 'Guardando...' : 'Registrar Comprobante'}
                </button>
              </div>
            </div>
            );
          })()}

          {/* Filtros de estado + búsqueda + fechas */}
          <div className="cm-filtros-bar">
            <div className="cm-filtros-estados">
              {[
                { key: 'pendiente', label: 'Pendientes', count: conteos.pendiente },
                { key: 'aprobado', label: 'Aprobados', count: conteos.aprobado },
                { key: 'rechazado', label: 'Rechazados', count: conteos.rechazado },
                { key: 'todos', label: 'Todos', count: comprobantes.length }
              ].map(f => (
                <button key={f.key}
                  className={`cm-filtro-btn ${filtroEstadoComp === f.key ? 'activo' : ''}`}
                  onClick={() => setFiltroEstadoComp(f.key)}>
                  {f.label} <span className="cm-filtro-count">{f.count}</span>
                </button>
              ))}
            </div>
            <input
              type="text" value={busquedaComp} onChange={e => setBusquedaComp(e.target.value)}
              placeholder="Buscar por residente, descripción, proyecto o apto..."
              className="cm-search-bar"
            />
          </div>
          <div className="cm-filtros-fechas">
            <div className="cm-fecha-grupo">
              <label>Desde</label>
              <input type="date" value={fechaDesdeComp} onChange={e => setFechaDesdeComp(e.target.value)} />
            </div>
            <div className="cm-fecha-grupo">
              <label>Hasta</label>
              <input type="date" value={fechaHastaComp} onChange={e => setFechaHastaComp(e.target.value)} />
            </div>
            {(fechaDesdeComp || fechaHastaComp) && (
              <button className="cm-btn cm-btn-sm cm-btn-secondary" onClick={() => { setFechaDesdeComp(''); setFechaHastaComp(''); }}>Limpiar fechas</button>
            )}
          </div>

          {comprobantesTabFiltrados.length === 0 ? (
            <div className="cm-empty">No hay comprobantes {filtroEstadoComp !== 'todos' ? filtroEstadoComp + 's' : ''}</div>
          ) : (
            <div className="cm-comprobantes-list">
              {comprobantesTabFiltrados.map(c => (
                <div key={c.id} className={`cm-comprobante-item cm-estado-${c.estado}`}>
                  <div className="cm-comp-header">
                    <span className="cm-comp-residente">{c.ingeniero_nombre}</span>
                    <span className={`cm-comp-badge cm-badge-${c.estado}`}>{c.estado}</span>
                  </div>
                  <p className="cm-comp-desc">{c.descripcion}</p>
                  <div className="cm-comp-meta">
                    <span>{formatFecha(c.fecha)}</span>
                    <span className="cm-comp-valor">{formatMoney(c.valor)}</span>
                  </div>
                  {c.proyectos_distribucion.length > 0 && (
                    <div className="cm-comp-distribucion">
                      <span className="cm-dist-label">{c.estado === 'aprobado' ? 'Distribuido:' : 'Por proyecto:'}</span>
                      {c.proyectos_distribucion.map(pd => (
                        <span key={pd.id} className="cm-dist-item">{pd.proyecto_nombre}{pd.apartamento ? ` · Apt ${pd.apartamento}` : ''}: {formatMoney(pd.valor_distribuido)}</span>
                      ))}
                    </div>
                  )}
                  {c.archivos.length > 0 && (
                    <div className="cm-comp-archivos">
                      {c.archivos.map(a => (
                        <a key={a.id} href={`${UPLOAD_URL}/caja-menor-file/${encodeURIComponent(a.ruta_archivo)}`} target="_blank" rel="noopener noreferrer" className="cm-archivo-link">{a.nombre_archivo}</a>
                      ))}
                    </div>
                  )}
                  {c.estado === 'pendiente' && (
                    <div className="cm-comp-actions">
                      <button className="cm-btn cm-btn-sm cm-btn-danger" onClick={() => eliminarComprobante(c.id)}>Eliminar</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* =================== TAB APROBACIONES =================== */}
      {tabActiva === 'aprobaciones' && permisoAprobaciones && (
        <div className="cm-tab-content">
          {/* Filtros y búsqueda */}
          <div className="cm-filtros-bar">
            <div className="cm-filtros-estados">
              {[
                { key: 'pendiente', label: 'Pendientes', count: conteos.pendiente },
                { key: 'aprobado', label: 'Aprobados', count: conteos.aprobado },
                { key: 'rechazado', label: 'Rechazados', count: conteos.rechazado },
                { key: 'todos', label: 'Todos', count: comprobantes.length }
              ].map(f => (
                <button key={f.key}
                  className={`cm-filtro-btn ${filtroEstadoAprob === f.key ? 'activo' : ''}`}
                  onClick={() => setFiltroEstadoAprob(f.key)}>
                  {f.label} <span className="cm-filtro-count">{f.count}</span>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
              <input type="text" value={busquedaAprob} onChange={e => setBusquedaAprob(e.target.value)}
                placeholder="Buscar por residente, descripción, proyecto o apto..."
                className="cm-search-bar" />
              <button
                type="button"
                className="cm-btn cm-btn-secondary"
                onClick={exportarAprobacionesExcel}
                disabled={comprobantesFiltrados.length === 0}
                title="Descargar Excel con los comprobantes mostrados"
                style={{ whiteSpace: 'nowrap' }}
              >
                📥 Excel
              </button>
            </div>
          </div>

          {comprobantesFiltrados.length === 0 ? (
            <div className="cm-empty">No hay comprobantes {filtroEstadoAprob !== 'todos' ? filtroEstadoAprob + 's' : ''}</div>
          ) : (
            <div className="cm-aprobaciones-list">
              {comprobantesFiltrados.map(c => (
                <div key={c.id} className={`cm-aprobacion-card cm-aprob-estado-${c.estado}`}>
                  <div className="cm-aprob-header">
                    <div>
                      <span className="cm-aprob-residente">{c.ingeniero_nombre}</span>
                      <span className="cm-aprob-fecha">{formatFecha(c.estado === 'aprobado' ? (c.aprobado_at || c.fecha) : c.fecha)}</span>
                      <span className={`cm-comp-badge cm-badge-${c.estado}`}>{c.estado}</span>
                      {c.es_administrativo && (
                        <span className="cm-comp-badge" style={{ background: '#dbeafe', color: '#1d4ed8', marginLeft: 6 }}>
                          Administrativo
                        </span>
                      )}
                    </div>
                    <span className="cm-aprob-valor">{formatMoney(c.valor)}</span>
                  </div>
                  <p className="cm-aprob-desc">{c.descripcion}</p>

                  {c.proyectos_distribucion.length > 0 && aprobandoId !== c.id && (
                    <div className="cm-comp-distribucion">
                      <span className="cm-dist-label">{c.estado === 'aprobado' ? 'Distribuido:' : 'Por proyecto:'}</span>
                      {c.proyectos_distribucion.map(pd => (
                        <span key={pd.id} className="cm-dist-item">{pd.proyecto_nombre}{pd.apartamento ? ` · Apt ${pd.apartamento}` : ''}: {formatMoney(pd.valor_distribuido)}</span>
                      ))}
                    </div>
                  )}

                  {c.archivos && c.archivos.length > 0 && (
                    <div className="cm-comp-archivos">
                      {c.archivos.map(a => (
                        <a key={a.id} href={`${UPLOAD_URL}/caja-menor-file/${encodeURIComponent(a.ruta_archivo)}`} target="_blank" rel="noopener noreferrer" className="cm-archivo-link">{a.nombre_archivo}</a>
                      ))}
                    </div>
                  )}

                  {c.estado === 'pendiente' && aprobandoId === c.id && c.es_administrativo && (
                    <div className="cm-distribucion-form">
                      <h4>Confirmar aprobación de gasto administrativo</h4>
                      <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '8px 0 12px 0' }}>
                        Este es un gasto administrativo por <strong>{formatMoney(c.valor)}</strong> — no requiere asignación a proyectos.
                      </p>
                      <div className="cm-dist-actions">
                        <button className="cm-btn cm-btn-secondary cm-btn-sm" onClick={() => { setAprobandoId(null); setDistribucion([]); }}>Cancelar</button>
                        <button className="cm-btn cm-btn-success cm-btn-sm" onClick={() => aprobarComprobante(c.id)} disabled={guardando}>
                          {guardando ? 'Aprobando...' : 'Aprobar gasto administrativo'}
                        </button>
                      </div>
                    </div>
                  )}

                  {c.estado === 'pendiente' && aprobandoId === c.id && !c.es_administrativo && (
                    <div className="cm-distribucion-form">
                      <h4>Confirmar distribución de {formatMoney(c.valor)}:</h4>
                      {distribucion.map((d, idx) => (
                        <div key={idx} className="cm-dist-row">
                          <select value={d.proyecto_id} onChange={e => actualizarDistribucion(idx, 'proyecto_id', e.target.value)}>
                            <option value="">Seleccionar proyecto...</option>
                            {proyectos.map(p => <option key={p.id} value={p.id}>{p.proyecto_nombre}{p.apartamento ? ` · Apt ${p.apartamento}` : ''} - {p.nombre_cliente}</option>)}
                          </select>
                          <input type="number" value={d.valor} onChange={e => actualizarDistribucion(idx, 'valor', e.target.value)} placeholder="Valor" />
                          {distribucion.length > 1 && <button className="cm-btn-icon cm-btn-danger" onClick={() => quitarProyectoDistribucion(idx)}><IconX /></button>}
                        </div>
                      ))}
                      <div className="cm-dist-total">
                        Total: {formatMoney(distribucion.reduce((s, d) => s + d.valor, 0))}
                        {Math.abs(distribucion.reduce((s, d) => s + d.valor, 0) - c.valor) > 0.01 && <span className="cm-dist-warn"> (debe ser {formatMoney(c.valor)})</span>}
                      </div>
                      <div className="cm-dist-actions">
                        <button className="cm-btn cm-btn-sm" onClick={agregarProyectoDistribucion}>+ Agregar proyecto</button>
                        <button className="cm-btn cm-btn-secondary cm-btn-sm" onClick={() => { setAprobandoId(null); setDistribucion([]); }}>Cancelar</button>
                        <button className="cm-btn cm-btn-success cm-btn-sm" onClick={() => aprobarComprobante(c.id)} disabled={guardando}>
                          {guardando ? 'Aprobando...' : 'Aprobar'}
                        </button>
                      </div>
                    </div>
                  )}

                  {c.estado === 'pendiente' && aprobandoId !== c.id && (
                    <div className="cm-aprob-actions">
                      <button className="cm-btn cm-btn-success cm-btn-sm" onClick={() => iniciarAprobacion(c)}>Aprobar</button>
                      <button className="cm-btn cm-btn-danger cm-btn-sm" onClick={() => rechazarComprobante(c.id)}>Rechazar</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CajaMenor;
